import { trackEvent } from './analyticsService';

// Safely check if in development mode
const isDev = () => {
    try {
        return import.meta?.env?.DEV || process?.env?.NODE_ENV === 'development' || false;
    } catch (e) {
        return false;
    }
};

class ErrorTrackingService {
    constructor() {
        this.initialized = false;
        this.errorCount = 0;
        this.maxErrorsPerSession = 50; // Prevent spam
    }

    init(app) {
        if (this.initialized) return;

        // Vue error handler
        if (app) {
            app.config.errorHandler = (err, instance, info) => {
                this.captureVueError(err, instance, info);
            };

            // Vue warning handler (only in dev)
            if (isDev()) {
                app.config.warnHandler = (msg, instance, trace) => {
                    console.warn('Vue warning:', msg, trace);
                };
            }
        }

        // Global error handler
        window.addEventListener('error', (event) => {
            this.captureError(event.error || new Error(event.message), {
                type: 'uncaught_error',
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno
            });
        });

        // Unhandled promise rejection handler
        window.addEventListener('unhandledrejection', (event) => {
            this.captureError(event.reason, {
                type: 'unhandled_promise_rejection',
                promise: event.promise
            });
        });

        // Network error handler (optional)
        this.setupNetworkErrorTracking();

        this.initialized = true;
        console.log('Error tracking initialized');
    }

    captureVueError(error, instance, info) {
        const errorData = this.parseError(error);
        
        this.trackError({
            ...errorData,
            error_type: 'vue_error',
            lifecycle_hook: info,
            component_name: instance?.$options?.name || 'Unknown',
            component_props: instance?.$props,
        });

        // Still log to console in development
        if (isDev()) {
            console.error('Vue Error:', error, info, instance);
        }
    }

    captureError(error, context = {}) {
        if (this.errorCount >= this.maxErrorsPerSession) {
            console.warn('Max errors reached for this session');
            return;
        }

        const errorData = this.parseError(error);
        
        this.trackError({
            ...errorData,
            ...context,
            error_type: context.type || 'javascript_error'
        });

        this.errorCount++;

        // Log to console in development
        if (isDev()) {
            console.error('Captured Error:', error, context);
        }
    }

    parseError(error) {
        if (!error) {
            return {
                message: 'Unknown error',
                stack: 'No stack trace available',
                name: 'UnknownError'
            };
        }

        if (typeof error === 'string') {
            return {
                message: error,
                stack: new Error(error).stack,
                name: 'StringError'
            };
        }

        return {
            message: error.message || String(error),
            stack: error.stack || 'No stack trace',
            name: error.name || 'Error',
            code: error.code,
            statusCode: error.response?.status,
            responseData: error.response?.data
        };
    }

    trackError(errorData) {
        try {
            // Track to PostHog
            trackEvent('error_occurred', {
                error_message: errorData.message,
                error_type: errorData.error_type,
                error_name: errorData.name,
                error_code: errorData.code,
                stack_trace: this.sanitizeStackTrace(errorData.stack),
                component_name: errorData.component_name,
                lifecycle_hook: errorData.lifecycle_hook,
                url: window.location.href,
                user_agent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                // HTTP error details if applicable
                status_code: errorData.statusCode,
                // Don't send full response data for privacy
                has_response_data: !!errorData.responseData
            });

            // Also log structured error for debugging
            console.debug('Error tracked:', {
                message: errorData.message,
                type: errorData.error_type,
                timestamp: new Date().toISOString()
            });
        } catch (trackingError) {
            // Never let error tracking break the app
            console.error('Failed to track error:', trackingError);
        }
    }

    sanitizeStackTrace(stack) {
        if (!stack) return 'No stack trace';
        
        // Limit stack trace length and remove sensitive info
        const lines = stack.split('\n').slice(0, 10); // First 10 lines
        return lines.join('\n');
    }

    setupNetworkErrorTracking() {
        // Intercept fetch errors
        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            try {
                const response = await originalFetch(...args);
                
                // Track HTTP errors
                if (!response.ok && response.status >= 400) {
                    this.captureError(new Error(`HTTP ${response.status}: ${response.statusText}`), {
                        type: 'network_error',
                        url: args[0],
                        status: response.status,
                        statusText: response.statusText,
                        method: args[1]?.method || 'GET'
                    });
                }
                
                return response;
            } catch (error) {
                this.captureError(error, {
                    type: 'network_error',
                    url: args[0],
                    method: args[1]?.method || 'GET'
                });
                throw error;
            }
        };
    }

    // Manual error reporting
    reportError(message, context = {}) {
        this.captureError(new Error(message), {
            ...context,
            type: 'manual_error'
        });
    }

    // Track handled errors (when you catch an error but want to log it)
    logHandledError(error, context = {}) {
        const errorData = this.parseError(error);
        
        trackEvent('handled_error', {
            error_message: errorData.message,
            error_name: errorData.name,
            error_code: errorData.code,
            context: context,
            timestamp: new Date().toISOString()
        });

        if (isDev()) {
            console.warn('Handled error logged:', error, context);
        }
    }

    // Get error statistics
    getErrorStats() {
        return {
            errorCount: this.errorCount,
            maxErrors: this.maxErrorsPerSession,
            remainingCapacity: this.maxErrorsPerSession - this.errorCount
        };
    }

    // Reset error count (useful for testing or after session)
    reset() {
        this.errorCount = 0;
    }
}

// Export singleton instance
export const errorTracking = new ErrorTrackingService();

// Export convenience methods
export const captureError = (error, context) => errorTracking.captureError(error, context);
export const reportError = (message, context) => errorTracking.reportError(message, context);
export const logHandledError = (error, context) => errorTracking.logHandledError(error, context);
export const initErrorTracking = (app) => errorTracking.init(app);
